/** Centralised API client. Every request goes through `api()` so errors and CSRF are handled uniformly. */

export const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') || '/api/v1';

export interface FieldIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details: FieldIssue[] = [],
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isNetworkError() {
    return this.code === 'NETWORK_ERROR';
  }
}

export interface Paginated<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

// The CSRF token is held in memory only (never in localStorage); it is re-fetched from /admin/auth/me on load.
let csrfToken: string | null = null;
export function setCsrfToken(token: string | null) {
  csrfToken = token;
}

type QueryValue = string | number | boolean | null | undefined | (string | number)[];

export function buildQuery(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) value.forEach((v) => search.append(key, String(v)));
    else search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

async function request(path: string, { method = 'GET', body, query, signal }: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(query)}`, {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      credentials: 'include',
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(0, 'NETWORK_ERROR', 'We could not reach the server. Please check your internet connection and try again.');
  }

  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string; details?: FieldIssue[]; requestId?: string } } = {};
    try {
      payload = await res.json();
    } catch {
      /* non-JSON error page from a proxy */
    }
    const e = payload.error ?? {};
    throw new ApiError(
      res.status,
      e.code ?? 'HTTP_ERROR',
      e.message ?? (res.status >= 500 ? 'The server encountered a problem. Please try again shortly.' : `Request failed (${res.status}).`),
      e.details ?? [],
      e.requestId,
    );
  }
  return res;
}

/** JSON request returning the `data` field of the response envelope. */
export async function api<T>(path: string, options?: RequestOptions): Promise<T> {
  const res = await request(path, options);
  const json = (await res.json()) as { data: T };
  return json.data;
}

/** JSON request returning `{ data, meta }` for paginated endpoints. */
export async function apiPage<T>(path: string, options?: RequestOptions): Promise<Paginated<T>> {
  const res = await request(path, options);
  return (await res.json()) as Paginated<T>;
}

/** Raw response, e.g. for CSV downloads. */
export function apiRaw(path: string, options?: RequestOptions): Promise<Response> {
  return request(path, options);
}
